/* import __COLOCATED_TEMPLATE__ from './walkthrough-popover-manager.hbs'; */
/* eslint "ember/no-get":off */

import { computed } from '@ember/object';
import { readOnly, or, equal } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import Component from '@ember/component';
import { FIXED_BOTTOM_RIGHT } from '@square/onboarding/models/walkthrough';

/**

## Overview

**Do not use this component directly!** It is intended to exist only in `application.hbs`.

Walkthrough Popover Manager controls the display of walkthrough popovers.
It is driven by the walkthrough service which controls the state.

See services/walkthrough.js for full documentation and usage.

## Usage

```handlebars
{{! Again, don't do this anywhere but application.hbs! }}
<WalkthroughPopoverManager />
```

*/

const POPOVER_FLY_UP_CLASS = 'popover--fly-up';
const POPOVER_FLY_RIGHT_CLASS = 'popover--fly-right';
const POPOVER_FLY_DOWN_CLASS = 'popover--fly-down';
const POPOVER_FLY_LEFT_CLASS = 'popover--fly-left';
const POINTER_CLASS = 'pointer';
const POPOVER_TOP_CLASS = 'popover--walkthrough--placement-top';
const POPOVER_RIGHT_CLASS = 'popover--walkthrough--placement-right';
const POPOVER_BOTTOM_CLASS = 'popover--walkthrough--placement-bottom';
const POPOVER_LEFT_CLASS = 'popover--walkthrough--placement-left';
const POINTER_ALIGN_TOP_CLASS = 'pointer--align-top';
const POINTER_ALIGN_RIGHT_CLASS = 'pointer--align-right';
const POINTER_ALIGN_BOTTOM_CLASS = 'pointer--align-bottom';
const POINTER_ALIGN_LEFT_CLASS = 'pointer--align-left';
const DARK_MODE_CLASS = 'popover--walkthrough--dark';

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  classNames: ['walkthrough-popover-manager'],
  classNameBindings: ['popoverHasNoTargetElement:walkthrough-popover-manager--no-target', 'isActive:walkthrough-popover-manager--active:walkthrough-popover-manager--inactive'],
  walkthrough: service(),
  currentWalkthrough: readOnly('walkthrough.currentWalkthrough'),
  currentStep: readOnly('currentWalkthrough.currentStep'),
  popoverComponent: or('currentStep.popoverComponent', 'currentWalkthrough.popoverComponent'),
  popoverHasNoTargetElement: equal('currentStep.targetElement', FIXED_BOTTOM_RIGHT),
  isActive: readOnly('currentWalkthrough.popoverIsActive'),
  progressText: null,
  inputClassNames: readOnly('currentWalkthrough.popoverClassNames'),
  popperPlacement: computed('currentStep.placement', function () {
    if (!this.currentStep) {
      return null;
    }

    // Placement specifies the popper position (e.g. bottom-start) as defined in https://popper.js.org/popper-documentation.html#Popper.placements
    // For the positioning classes we just care about bottom/right/left/top
    return this.get('currentStep.placement').split('-')[0];
  }),
  popperAlignment: computed('currentStep.placement', function () {
    if (!this.currentStep) {
      return null;
    }

    // Placement specifies the popper position (e.g. bottom-start) as defined in https://popper.js.org/popper-documentation.html#Popper.placements
    return this.get('currentStep.placement').split('-')[1];
  }),
  popoverPositionClass: computed('popperPlacement', function () {
    switch (this.popperPlacement) {
      case 'top':
        return POPOVER_TOP_CLASS;
      case 'right':
        return POPOVER_RIGHT_CLASS;
      case 'bottom':
        return POPOVER_BOTTOM_CLASS;
      case 'left':
        return POPOVER_LEFT_CLASS;
      default:
        return null;
    }
  }),
  directionClass: computed('currentStep.animate', function () {
    switch (this.get('currentStep.animate')) {
      case 'fly-up':
        return POPOVER_FLY_UP_CLASS;
      case 'fly-down':
        return POPOVER_FLY_DOWN_CLASS;
      case 'fly-right':
        return POPOVER_FLY_RIGHT_CLASS;
      case 'fly-left':
        return POPOVER_FLY_LEFT_CLASS;
      default:
        return null;
    }
  }),
  pointerClass: computed('currentStep.pointerEnabled', 'popperPlacement', function () {
    if (!this.get('currentStep.pointerEnabled')) {
      return null;
    } else {
      return POINTER_CLASS;
    }
  }),
  pointerAlignClass: computed('currentStep.{placement,pointerEnabled}', 'popperAlignment', 'popperPlacement', function () {
    if (!this.get('currentStep.pointerEnabled')) {
      return null;
    }

    // If there is a start or end modifier
    const popperPlacement = this.popperPlacement;
    const popperAlignment = this.popperAlignment;
    switch (popperAlignment) {
      case 'start':
        if (popperPlacement === 'bottom' || popperPlacement === 'top') {
          return POINTER_ALIGN_LEFT_CLASS;
        } else {
          return POINTER_ALIGN_TOP_CLASS;
        }
      case 'end':
        if (popperPlacement === 'bottom' || popperPlacement === 'top') {
          return POINTER_ALIGN_RIGHT_CLASS;
        } else {
          return POINTER_ALIGN_BOTTOM_CLASS;
        }
      default:
        return null;
    }
  }),
  darkModeClass: DARK_MODE_CLASS,
  popoverClassNames: computed('directionClass', 'pointerClass', 'pointerAlignClass', 'popoverPositionClass', 'darkModeClass', 'inputClassNames', function () {
    const classes = [this.pointerClass, this.directionClass, this.pointerAlignClass, this.popoverPositionClass];
    if (this.darkModeClass) {
      classes.push(this.darkModeClass);
    }
    if (this.inputClassNames) {
      classes.push(...this.inputClassNames.split(' '));
    }
    return classes.join(' ');
  }),
  actions: {
    next() {
      this.currentWalkthrough.completeStep(this.get('currentStep.name'));
    },
    close() {
      if (this.currentWalkthrough.onClose) {
        this.currentWalkthrough.onClose(this.currentStep.name);
      }
      this.walkthrough.stop();
    }
  }
});